import React, { useEffect, useState } from 'react';
import { Minus, Square, X, Copy } from 'lucide-react';
import { useTenant } from "@/shared/config/tenant";

import { isTauriRuntime } from "@/shared/lib/tauri";
/**
 * TitleBar component for Tauri application.
 * Provides window controls (minimize, maximize, close) and a draggable region.
 */
const TitleBar = () => {
    const tenant = useTenant();
  const [isMaximized, setIsMaximized] = useState(false);
  const [isTauri, setIsTauri] = useState(false);

  useEffect(() => {
    // Check if we are running in Tauri
    const checkTauri = () => {
      return isTauriRuntime();
    };

    const runningInTauri = checkTauri();
    setIsTauri(runningInTauri);

    if (runningInTauri) {
      let unlistenFn: (() => void) | undefined;

      const initTauri = async () => {
        try {
          const { getCurrentWindow } = await import('@tauri-apps/api/window');
          const win = getCurrentWindow();

          const maximized = await win.isMaximized();
          setIsMaximized(maximized);

          unlistenFn = await win.onResized(async () => {
            const isMax = await win.isMaximized();
            setIsMaximized(isMax);
          });
        } catch (error) {
          console.error('Failed to initialize Tauri window:', error);
        }
      };

      initTauri();

      return () => {
        if (unlistenFn) unlistenFn();
      };
    }
  }, []);

  const handleMinimize = async () => {
    if (!isTauri) return;
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      await getCurrentWindow().minimize();
    } catch (err) {
      console.error(err);
    }
  };

  const handleMaximize = async () => {
    if (!isTauri) return;
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      await getCurrentWindow().toggleMaximize();
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = async () => {
    if (!isTauri) return;
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      await getCurrentWindow().close();
    } catch (err) {
      console.error(err);
    }
  };

  if (!isTauri) return null;

  return (
    <div
      data-tauri-drag-region
      className="h-9 w-full bg-white dark:bg-night border-b border-light_gray/50 dark:border-white/[0.06] flex items-center justify-between select-none z-titlebar shrink-0"
    >
      {/* Draggable Logo Area */}
      <div 
        data-tauri-drag-region 
        className="flex items-center px-4 h-full cursor-default"
      >
        <img src={tenant.logoUrl} alt={`${tenant.name}`} className="h-[18px] w-auto mr-3 object-contain pointer-events-none" />
        <span className="text-[11px] font-bold text-dark_green dark:text-zinc-300 uppercase tracking-widest pointer-events-none">
          7Days - Gestão de Obras
        </span>
      </div>

      {/* Draggable Empty Space */}
      <div data-tauri-drag-region className="flex-1 h-full cursor-default" />

      {/* Window Controls */}
      <div className="flex h-full">
        <button
          onClick={handleMinimize}
          className="px-4 h-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-night_input transition-colors"
          title="Minimizar"
        >
          <Minus size={15} className="text-gray-600 dark:text-zinc-400" />
        </button>
        <button
          onClick={handleMaximize}
          className="px-4 h-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-night_input transition-colors"
          title={isMaximized ? "Restaurar" : "Maximizar"}
        >
          {isMaximized ? (
            <Copy size={13} className="text-gray-600 dark:text-zinc-400" />
          ) : (
            <Square size={13} className="text-gray-600 dark:text-zinc-400" />
          )}
        </button>
        <button
          onClick={handleClose}
          className="px-4 h-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors group"
          title="Fechar"
        >
          <X size={15} className="text-gray-600 dark:text-zinc-400 group-hover:text-white" />
        </button>
      </div>
    </div>
  );
};

export default TitleBar;
